/**
 * Sits above everything else in `App.tsx`, before the query cache or the
 * session provider — `ConsoleSessionProvider`'s first act is `GET /me`, and
 * that call needs the auth token provider installed already or it goes out
 * unauthenticated.
 *
 * When `entraEnabled` is false (every local-dev build, since
 * `VITE_ENTRA_CLIENT_ID` is never set there), this renders straight through —
 * the API's `AUTH_DEV_BYPASS` is what authenticates in that world, and MSAL
 * never loads at all.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Card } from '../components/card';
import { Button } from '../components/button';
import { acquireAccessToken, ensureSignedIn, entraEnabled, signIn } from '../lib/auth';
import { setAuthTokenProvider } from '../lib/api';
import styles from './auth-gate.module.css';

type GateState = 'checking' | 'signed-out' | 'signed-in';

export function AuthGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<GateState>(entraEnabled ? 'checking' : 'signed-in');

  useEffect(() => {
    if (!entraEnabled) return;
    let cancelled = false;
    ensureSignedIn()
      .then((account) => {
        if (cancelled) return;
        if (account) setAuthTokenProvider(acquireAccessToken);
        setState(account ? 'signed-in' : 'signed-out');
      })
      .catch(() => {
        if (!cancelled) setState('signed-out');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state === 'checking') {
    return (
      <div className={styles.wrap}>
        <p className={styles.body}>Signing you in…</p>
      </div>
    );
  }

  if (state === 'signed-out') {
    return (
      <div className={styles.wrap}>
        <Card className={styles.card}>
          <span className={styles.mark} aria-hidden="true">
            iQ
          </span>
          <h1 className={styles.title}>Sign in to Otto IQ</h1>
          <p className={styles.body}>Use your Microsoft work account to continue.</p>
          <Button variant="primary" block onClick={signIn}>
            Sign in with Microsoft
          </Button>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
